/**
 * @module composables/useStatus
 *
 * Composable for monitoring server-side job queue status.
 * Fetches the current state of the video, monitor, metadata, transcript,
 * and summary queues, and provides automatic polling that starts/stops
 * based on whether there are active jobs.
 *
 * All refs are module-scoped singletons so status data persists across
 * component navigation (e.g., StatusView shows current queue state).
 */

import { ref, computed, watch } from "vue";
import { api } from "../api/config.js";

/** The current queue status object from the server, containing video/monitor/metadata queues */
const taskStatus = ref();

/**
 * 'mine' (default) or 'all' — which endpoint getStatus() hits. StatusView
 * sets this (via its route's `allUsers` meta) BEFORE its first fetch/poll;
 * the auto-poll watcher's own no-args getStatus() calls respect whatever
 * this is currently set to, so admin-status polling stays on the all-users
 * endpoint without threading a mode through every call site. StatusView
 * resets this to 'mine' on unmount so leaving the admin page can't leave
 * the singleton polling the wrong endpoint for a later visit to /status.
 */
const statusMode = ref("mine");

/** The setInterval ID for status polling, or null/undefined when not polling */
const pollInterval = ref();

/** Polling interval in milliseconds (6 seconds) */
const POLLINTERVAL = 6 * 1000;

/** Whether a status fetch is currently in progress */
const loadingStatus = ref(false);

/**
 * Helper to sum numeric values, treating null/undefined as 0.
 * Used for counting total jobs across multiple queue categories.
 *
 * @param {...(number|null|undefined)} values - Values to sum
 * @returns {number} The sum of all values
 */
function sumValues(...values) {
  return values.reduce((sum, value) => sum + (value ?? 0), 0);
}

/**
 * Computed total of active + waiting jobs across all queues
 * (video, monitor, metadata). Used to determine if polling should
 * start or stop.
 */
const totalJobs = computed(() => {
  if (taskStatus.value) {
    return sumValues(
      taskStatus.value.video.active.length,
      taskStatus.value.video.waiting.length,
      taskStatus.value.monitor.active.length,
      taskStatus.value.monitor.waiting.length,
      taskStatus.value.metadata.active.length,
      taskStatus.value.metadata.waiting.length,
      // AI summary generation (routes/episodes.js's summarize endpoint,
      // queued through the "summary" queue) — see StatusView.vue.
      taskStatus.value.summary?.active.length,
      taskStatus.value.summary?.waiting.length,
    );
  }
  return 0
});

/**
 * Computed total of completed jobs across all queues.
 * Used by StatusView to show the "Recent Activity" section.
 */
const totalCompletedJobs = computed(() => {
  if (taskStatus.value) {
    return sumValues(
      taskStatus.value.video.completed.length,
      taskStatus.value.metadata.completed.length,
      taskStatus.value.monitor.completed.length,
      taskStatus.value.summary?.completed.length,
    );
  }
  return 0;
});

/**
 * Computed total of failed jobs across all queues.
 * Displayed in the StatusView as a separate "failed jobs" section.
 */
const totalFailedJobs = computed(() => {
  if (taskStatus.value) {
    return sumValues(
      taskStatus.value.video.failed.length,
      taskStatus.value.metadata.failed.length,
      taskStatus.value.monitor.failed.length,
      taskStatus.value.summary?.failed.length,
    );
  }
  return 0
});

/**
 * Clear all completed and failed jobs from the server queue, plus
 * any persistent episode error messages (progress_message) in the DB.
 * Called by the "Clear" button in StatusView.
 */
const clearFailedJobs = async () => {
  await api.post("/queue/delete-jobs");
  await getStatus();
};

/**
 * Watcher on totalJobs that automatically manages polling:
 * - When jobs drop to 0: does a final status fetch, then stops polling
 *   (unless there are failed jobs still to display)
 * - When jobs appear (>0) and polling is not running: starts polling
 */
watch(totalJobs, async (val, oldVal) => {
  if (val == 0 && pollInterval.value) {
    // All jobs completed -- do one final fetch to catch the latest state
    await getStatus();
    // Only stop polling if there are no failed jobs left to display
    if (totalFailedJobs.value == 0) {
      stopPolling();
    }
  } else if (val > 0 && !pollInterval.value) {
    // New jobs appeared -- start polling for updates
    await startPolling();
  }
});

/**
 * Start polling the server for queue status at the configured interval.
 */
const startPolling = async () => {
  pollInterval.value = setInterval(async () => await getStatus(), POLLINTERVAL);
};

/**
 * Stop the status polling interval and clean up.
 * Uses null assignment (not delete) to properly clear the Vue ref.
 */
const stopPolling = () => {
  if (pollInterval.value) {
    clearInterval(pollInterval.value);
    pollInterval.value = null;
  }
};

/**
 * Fetch the current queue status from the server.
 * Supports optional filtering by queue name and/or queue status. When
 * `statusMode` is 'all' (admin-only Status-for-all-users page), those
 * filters are ignored — GET /queue/status/all-users doesn't support them —
 * and every job comes back tagged with a `userEmail` field.
 *
 * @param {string} [queueName] - Optional queue name to filter ("video", "monitor", "metadata")
 * @param {string} [queueStatus] - Optional status to filter ("active", "waiting", "failed")
 */
const getStatus = async (queueName, queueStatus) => {
  loadingStatus.value = true;
  // Build the endpoint URL based on optional filter parameters
  let endpoint;
  if (statusMode.value === "all") {
    endpoint = `/queue/status/all-users`;
  } else if (queueName && !queueStatus) {
    // Filter by queue name only (e.g., /queue/status/video)
    endpoint = `/queue/status/${queueName}`;
  } else if (queueName && queueStatus) {
    // Filter by both queue name and status (e.g., /queue/status/video/active)
    endpoint = `/queue/status/${queueName}/${queueStatus}`;
  } else {
    // No filter -- get all queues' status
    endpoint = `/queue/status`;
  }
  try {
    const response = await api.get(endpoint);
    taskStatus.value = response.data;
    loadingStatus.value = false;
  } catch (error) {
    console.error(error);
    loadingStatus.value = false;
    throw new Error(error.response?.data?.message || "Failed to get status");
  } finally {
  }
};

/**
 * Composable function returning all queue status state and functions.
 *
 * @returns {Object} Status state refs, computed properties, and control functions
 */
export function useStatus() {
  return {
    statusMode,
    totalJobs,
    totalCompletedJobs,
    totalFailedJobs,
    getStatus,
    taskStatus,
    loadingStatus,
    pollInterval,
    stopPolling,
    startPolling,
    clearFailedJobs,
  };
}
